import React, { Fragment, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { differenceInDays } from 'date-fns';

import { MainContainer, MainTitle, TitleContainer, ColumnContainer, Column, Paragraph, Breadcrumbs, HeadSeo, LoadingContainer, Divider, CardContainer, ButtonsRow, MainButton, SectionTitle, NormalPopup, SimpleLink } from '@beacharound/beacharound-ui';
import { getProductImage, formatDate } from '@beacharound/beacharound-ui-helpers';

import { getPlaceholderImage } from '../../helpers/images/getPlaceholderImage';
import { getGeoBreadCrumbs } from '../../helpers/getGeoBreadCrumbs';
import { getProducts } from '../../api/products';
import { getBeach } from '../../api/beach';
import { createView } from '../../api/views';
import { getUser } from '../../api/user';

import ReviewsPlaceCount from '../../components/Reviews/ReviewsPlaceCount';
import NotFoundRedirect from '../../components/NotFound/NotFoundRedirect';
import UmbrellasList from '../../components/PriceLists/UmbrellasList';
import DeckchairsList from '../../components/PriceLists/DeckchairsList';
import BundlesList from '../../components/PriceLists/BundlesList';
import ServicesList from '../../components/Services/ServicesList';
import ParkingsList from '../../components/PriceLists/ParkingsList';
import BookingCalendar from '../../components/Beaches/Booking/BookingCalendar';
import ProductsTable from '../../components/PriceLists/ProductsTable';
import GalleryComponent from '../../components/Gallery/GalleryComponent';
import PlacesInfoAddress from '../../components/PlacesInfo/PlacesInfoAddress';
import BeachesPhonePopup from '../../components/Beaches/BeachesPhonePopup';
import SignUserComponent from '../../components/User/SignUserComponent';
import AccessibilitiesList from '../../components/Accessibilities/AccessibilitiesList';
import BeachesPromotionsHighlight from '../../components/Beaches/BeachesPromotionsHighlight';
import PrerenderMeta from '../../components/Prerender/PrerenderMeta';

export default function BeachPriceList({ match, location }) {

    let allProducts = [];
    const { beachSlug } = match.params;
    const [searching, setSearching] = useState();
    const [callPopup, setCallPopup] = useState();
    const [signPopup, setSignPopup] = useState();

    // SPIAGGIA
    const { data: beach, error, isLoading } = useQuery(["beaches", beachSlug], () => getBeach({ slug: beachSlug }));
    const { data: user } = useQuery("user", getUser);

    const beachFromDate = beach?.availability?.fromDate;
    const beachToDate = beach?.availability?.toDate;
    const daysNum = differenceInDays(new Date(beachToDate), new Date(beachFromDate));

    const queryOptions = {
        enabled: !!beachFromDate && !!beachToDate,
        refetchOnWindowFocus: false
    };

    const options = {
        limit: 200,
        fromDate: new Date(beachFromDate),
        toDate: new Date(beachToDate)
    };

    // BUNDLES
    const { data: bundles, isLoading: isLoadingBundles } = useQuery(["beaches", beachSlug, "products", "bundles", options], () =>
        getProducts({ activitySlug: beachSlug, params: { bundleType: 'normal' }, ...options }),
        queryOptions
    );

    // OMBRELLONI
    const { data: umbrellas, isLoading: isLoadingUmbrellas } = useQuery(["beaches", beachSlug, "products", "umbrellas", options], () =>
        getProducts({ activitySlug: beachSlug, params: { type: 'umbrella', bundleType: { $exists: false } }, ...options }),
        queryOptions
    );

    // LETTINI
    const { data: deckchairs, isLoading: isLoadingDeckchairs } = useQuery(["beaches", beach?.slug, "products", "deckchairs", options], () =>
        getProducts({ activitySlug: beachSlug, params: { type: 'deckchair', bundleType: { $exists: false } }, ...options }),
        queryOptions
    );

    // SERVIZI
    const { data: services, isLoading: isLoadingServices } = useQuery(["beaches", beach?.slug, "products", "services", options], () =>
        getProducts({ activitySlug: beachSlug, params: { type: 'service', bundleType: { $exists: false } }, ...options }),
        queryOptions
    );

    // PARCHEGGI
    const { data: parkings, isLoading: isLoadingParkings } = useQuery(["beaches", beach?.slug, "products", "parkings", options], () =>
        getProducts({ activitySlug: beachSlug, params: { type: 'parking', bundleType: { $exists: false } }, ...options }),
        queryOptions
    );

    const { mutate, status } = useMutation(createView, {
        onSuccess: (data) => {
            if (!user) {
                return setSignPopup(true)
            }
            return setCallPopup(true)
        }
    });

    if (bundles?.length > 0) {
        allProducts = [...allProducts, ...bundles];
    }
    if (umbrellas?.length > 0) {
        allProducts = [...allProducts, ...umbrellas];
    }
    if (deckchairs?.length > 0) {
        allProducts = [...allProducts, ...deckchairs];
    }
    if (services?.length > 0) {
        allProducts = [...allProducts, ...services];
    }
    if (parkings?.length > 0) {
        allProducts = [...allProducts, ...parkings];
    }

    const beachName = beach?.name || 'Spiaggia';
    const cityName = beach?.address?.city;

    const seoAdditions = {
        "itemListElement": allProducts?.map((prod, index) => {

            const lowPrice = daysNum ? Math.floor(prod.price / daysNum).toFixed(2) : Math.floor(prod.price).toFixed(2);
            const highPrice = Math.floor(prod.price).toFixed(2);

            let images;

            if (!!prod.relatedProducts?.length) {
                images = prod.relatedProducts?.map(item => getProductImage(item.reference?.type));
            } else {
                images = prod.type && [getProductImage(prod.type)];
            }

            if (!images) {
                images = [getProductImage('umbrella')];
            }

            return {
                "@type": "ListItem",
                "position": index + 1,
                "item": {
                    "@type": "Product",
                    "name": prod.name,
                    "image": images,
                    "url": location.href,
                    "brand": {
                        "@type": "Brand",
                        "name": beachName
                    },
                    "offers": {
                        "@type": "AggregateOffer",
                        "price": lowPrice,
                        "lowPrice": lowPrice,
                        "highPrice": highPrice,
                        "priceCurrency": "EUR",
                        "offerCount": prod.quantity,
                        [beach?.purchasability?.fromDate && "priceValidUntil"]: beach?.purchasability?.fromDate && formatDate(new Date(beach?.purchasability?.fromDate), "yyyy-MM-dd"),
                        "itemCondition": "https://schema.org/NewCondition",
                        "availability": "https://schema.org/InStock"
                    }
                }
            };
        })
    }

    async function callAndBook() {
        await mutate({
            type: 'phone',
            beachSlug: beach?.slug
        });
    }

    return (
        <MainContainer>
            <NotFoundRedirect error={error} />

            <LoadingContainer isLoading={isLoading}>
                <HeadSeo
                    title={[`${beachName} ${cityName} prezzi ombrellone, lettino e sdraio`]}
                    name={`Prezzi ombrellone lettino sdraio di ${beachName} ${cityName}`}
                    descriptionFull={beach?.description}
                    description={`Prenota il tuo ombrellone nelle spiaggia di ${beachName} ${cityName} al miglior prezzo online. Scegli tra sdraio o lettino a tutto il resto ci pensa Beacharound!`}
                    type={"ItemList"}
                    image={beach?.cover || getPlaceholderImage("beaches", beach)}
                    additions={seoAdditions}
                />

                <PrerenderMeta isLoading={isLoading || isLoadingBundles || isLoadingDeckchairs || isLoadingParkings || isLoadingServices || isLoadingUmbrellas} />

                {beach?.bookable ?
                    <Fragment>
                        <TitleContainer>
                            <Breadcrumbs
                                breadcrumbs={beach?.geoElements && getGeoBreadCrumbs(beach?.geoElements, {
                                    beaches: true, others: [{
                                        label: beachName,
                                        url: match.url.replace("/prezzi-ombrellone-lettino-sdraio", "")
                                    }, {
                                        label: "Prezzi ombrellone, lettino e sdraio",
                                        url: match.url
                                    }]
                                })}
                                linkComponent={Link}
                            />
                            <MainTitle text={`Prezzi ombrellone, lettino e sdraio di <strong>${beachName}</strong>`} component="h1" />
                            <ReviewsPlaceCount place={beach?._id} />
                        </TitleContainer>

                        {beach?.type === 'private' && !beach?.onlySeasonal &&
                            <Fragment>
                                <BeachesPromotionsHighlight
                                    match={match}
                                />
                                <ColumnContainer lowMargin>
                                    <Column>
                                        <LoadingContainer isLoading={searching} loadingText={searching && 'Stiamo cercando i posti disponibili...'}>
                                            <TitleContainer lowMargin>
                                                <Paragraph center text="Prenota subito nelle date che preferisci!" />
                                            </TitleContainer>
                                            <BookingCalendar
                                                beach={beach}
                                                setSearching={setSearching}
                                                hideSeasonal
                                            />
                                            <Divider />
                                        </LoadingContainer>
                                    </Column>
                                </ColumnContainer>
                            </Fragment>
                        }

                        <BundlesList data={bundles} isLoading={isLoadingBundles} daysNum={daysNum} hidePrice />
                        <UmbrellasList data={umbrellas} isLoading={isLoadingUmbrellas} daysNum={daysNum} hidePrice />
                        <DeckchairsList data={deckchairs} isLoading={isLoadingDeckchairs} daysNum={daysNum} hidePrice />
                        <ServicesList data={services} isLoading={isLoadingServices} daysNum={daysNum} />
                        <ParkingsList data={parkings} isLoading={isLoadingParkings} daysNum={daysNum} hidePrice />

                        <Divider />

                        <ProductsTable
                            beachSlug={beachSlug}
                            data={allProducts}
                            beach={beach}
                            isLoading={isLoadingBundles || isLoadingUmbrellas || isLoadingDeckchairs || isLoadingServices || isLoadingParkings}
                            daysNum={daysNum}
                            hidePrice
                        />
                    </Fragment>
                    :
                    <Fragment>
                        <TitleContainer>
                            <Breadcrumbs
                                breadcrumbs={beach?.geoElements && getGeoBreadCrumbs(beach?.geoElements, {
                                    beaches: true, others: [{
                                        label: beachName,
                                        url: match.url.replace("/prezzi-abbontamenti-stagionali-ombrellone-lettino-sdraio", "")
                                    },
                                    {
                                        label: "Prezzi ombrellone, lettino e sdraio",
                                        url: match.url
                                    }]
                                })}
                                linkComponent={Link}
                            />
                            <MainTitle text={`Prezzi ombrellone, lettino e sdraio di <strong>${beachName}</strong>`} component="h1" />
                            <ReviewsPlaceCount place={beach?._id} />
                        </TitleContainer>

                        {beach && !beach.hasOwner &&
                            <ColumnContainer lowMargin>
                                <Column>
                                    <CardContainer>
                                        <Paragraph
                                            text="Il listino prezzi ombrellone, lettino e sdraio di questa spiaggia non è ancora disponibile su Beacharound. Il contenuto di questa pagina è frutto di ricerche e materiale raccolto dagli utenti.<br/> <strong>Sei il proprietario di questa spiaggia?</strong>"
                                            component="div"
                                        />
                                        <SimpleLink
                                            text="Gestisci questa spiaggia"
                                            component="a"
                                            href={`${process.env.REACT_APP_BUSINESS_URL}/user/signup?activityId=${beach?._id}`}
                                            target="_blank"
                                        />
                                    </CardContainer>
                                </Column>
                            </ColumnContainer>
                        }

                        <ColumnContainer lowMargin>
                            <Column>
                                <GalleryComponent
                                    cover={beach?.cover}
                                    gallery={beach?.gallery}
                                    name={beachName}
                                />

                                <ButtonsRow fill>
                                    {beach?.type === 'private' && beach?.hasPhone &&
                                        <MainButton
                                            text="Chiama e prenota"
                                            border
                                            action={() => callAndBook()}
                                            status={status}
                                        />
                                    }
                                    <MainButton
                                        border
                                        text="Racconta la tua esperienza!"
                                        component={Link}
                                        to={`/spiagge/${beach?.slug}/recensioni/nuova`}
                                    />
                                </ButtonsRow>
                            </Column>
                        </ColumnContainer>

                        <ColumnContainer lowMargin>
                            <Column>
                                <Divider noMargin />
                                <PlacesInfoAddress address={beach?.address} />
                                <Divider noMargin />
                            </Column>
                        </ColumnContainer>

                        {beach?.services?.length > 0 &&
                            <ServicesList services={beach.services} />
                        }

                        {beach?.accessibilities?.length > 0 && (
                            <AccessibilitiesList accessibilities={beach.accessibilities} />
                        )}

                        {beach && !beach.hasOwner &&
                            <ColumnContainer>
                                <Column>
                                    <CardContainer dark>
                                        <TitleContainer>
                                            <SectionTitle center color="white" text="Sei il gestore di questa spiaggia?" component="div" />
                                            <Paragraph center color="white" text="<strong>Registrati gratuitamente</strong> per tenere aggiornate le sue informazioni, caricare le foto, rispondere alle recensioni e tanto altro ancora!" component="div" />
                                        </TitleContainer>

                                        <ButtonsRow center>
                                            <MainButton
                                                color="white"
                                                text="Prendila in carico!"
                                                component="a"
                                                to={`${process.env.REACT_APP_BUSINESS_URL}/user/signup?activityId=${beach?._id}`}
                                                blank
                                            />
                                        </ButtonsRow>
                                    </CardContainer>
                                </Column>
                            </ColumnContainer>
                        }
                    </Fragment>
                }

                <TitleContainer>
                    <SectionTitle text={`Prenota il tuo ombrellone, sdraio o lettino nelle spiaggia di ${beachName} ${cityName} al miglior prezzo online.`} component="h2" />
                    <Paragraph text={`Prenota sempre il miglior prezzo online a ${beachName} ${cityName}. Cosa scegliere: sdraio o lettino? l'importante è avere un posto riservato con l'ombrellone al miglior prezzo online!<br/>Basta code e stressanti attese sotto il sole ma solo pace e relax. Non ti dovrai più preoccupare di nulla e potrai dedicarti unicamente a divertirti e rilassarti.<br/>Dai un occhio qui di seguito ai bundle e approfitta delle promozioni per trovare il servizio più adatto alle tue esigenze; a tutto il resto ci pensa Beacharound!`} component="p" />
                </TitleContainer>
            </LoadingContainer>

            <BeachesPhonePopup
                visible={callPopup}
                setVisible={setCallPopup}
                beach={beach}
            />

            <NormalPopup
                visible={signPopup}
                setVisible={setSignPopup}
                title="Accedi per chiamare"
            >
                <SignUserComponent
                    noMargin
                    match={match}
                    loginCallback={() => {
                        setSignPopup();
                        setCallPopup(true);
                    }}
                    signupCallback={() => {
                        setSignPopup();
                        setCallPopup(true);
                    }}
                />
            </NormalPopup>
        </MainContainer>
    );
}